/**
 * Track B — shared timezone list fetch + cache.
 *
 * Single source of truth for the IANA list surfaced in the signup
 * form, customer settings page, and provider profile page. Fetched
 * once per browser session and cached at module scope so subsequent
 * mounts don't re-hit the network.
 *
 * Fetch failure fallback: a small curated list of high-frequency
 * timezones so signup is never blocked by a transient outage. A
 * warning is logged to the console when the fallback is used.
 */
import { api } from "@/lib/api";

// Curated fallback — covers the majority of US traffic + a few
// international zones the founders may see in early adopters.
const FALLBACK_TIMEZONES = [
  "UTC",
  "America/Anchorage",
  "America/Chicago",
  "America/Denver",
  "America/Detroit",
  "America/Honolulu",
  "America/Indiana/Indianapolis",
  "America/Los_Angeles",
  "America/New_York",
  "America/Phoenix",
  "America/Puerto_Rico",
  "Australia/Sydney",
  "Europe/Berlin",
  "Europe/London",
  "Europe/Paris",
  "Asia/Kolkata",
  "Asia/Singapore",
  "Asia/Tokyo",
];

let _cached = null;
let _inflight = null;

export async function fetchTimezones() {
  if (_cached) return _cached;
  if (_inflight) return _inflight;
  _inflight = (async () => {
    try {
      const { data } = await api.get("/timezones");
      if (Array.isArray(data) && data.length > 0) {
        _cached = data.slice();
        return _cached;
      }
      // eslint-disable-next-line no-console
      console.warn("Empty /timezones payload; using curated fallback.");
      _cached = FALLBACK_TIMEZONES.slice();
      return _cached;
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn(
        "Failed to fetch /timezones; using curated fallback:",
        err?.message || err,
      );
      _cached = FALLBACK_TIMEZONES.slice();
      return _cached;
    } finally {
      _inflight = null;
    }
  })();
  return _inflight;
}

/**
 * Browser-detected IANA timezone via `Intl.DateTimeFormat`. Returns
 * an empty string when the browser doesn't support Intl (very old
 * browsers) or when a privacy shield stubs it to undefined — callers
 * must treat empty as "user must pick explicitly".
 */
export function detectBrowserTimezone() {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return typeof tz === "string" ? tz : "";
  } catch (_) {
    return "";
  }
}

// Test-only exports so precedence / fallback tests can seed state.
export const __test_reset = () => {
  _cached = null;
  _inflight = null;
};
export const __test_FALLBACK_TIMEZONES = FALLBACK_TIMEZONES;
